import type { BlockTransformAxis } from '@shared/protocol';
import type {
  FaceUiArrow,
  FaceUiAxes,
  FaceUiAxisVector,
  FaceUiColorScheme,
  FaceUiContentLayout,
  FaceUiDefinition,
  FaceUiFaceFilter,
  FaceUiIconAsset,
  FaceUiPoint,
} from '../ui/FaceUi';
import rawConfig from '../config/ui/face-ui/transform.json';
import rawTemplates from '../config/ui/face-ui/templates.json';

interface ConfigIcon {
  alias?: string;
  src?: string;
  rotation?: number;
  fallback?: FaceUiArrow;
}

type ConfigIconReference = string | ConfigIcon | false;

interface TransformFaceUiConfig {
  id: string;
  defaultLayout: string;
  faces?: FaceUiFaceFilter;
  colors?: FaceUiColorScheme;
  icons?: Record<string, Omit<ConfigIcon, 'alias'>>;
  layouts: Array<{
    id: string;
    hotkey?: string;
    momentary?: boolean;
    template: string;
    colors?: FaceUiColorScheme;
    content?: FaceUiContentLayout;
    buttons: Array<{
      id: string;
      text?: string;
      tooltip?: string;
      icon?: ConfigIconReference;
      content?: FaceUiContentLayout;
    }>;
  }>;
}

const config = rawConfig as unknown as TransformFaceUiConfig;
const templates = rawTemplates as unknown as Record<string, FaceUiPoint[][]>;

export const TRANSFORM_FACE_UI_ID = config.id;

export const TRANSFORM_FACE_UI: FaceUiDefinition = {
  id: config.id,
  faces: config.faces,
  defaultLayout: config.defaultLayout,
  colors: config.colors,
  layouts: config.layouts.map((layout) => {
    const slots = templates[layout.template];
    if (!slots || slots.length !== layout.buttons.length) {
      throw new Error(
        `Face UI template "${layout.template}" needs ${layout.buttons.length} slots`,
      );
    }
    return {
      id: layout.id,
      hotkey: layout.hotkey,
      momentary: layout.momentary,
      colors: layout.colors,
      content: layout.content,
      buttons: layout.buttons.map((button, index) => ({
        id: button.id,
        text: button.text,
        tooltip: button.tooltip,
        content: button.content,
        polygon: slots[index],
        icon: configuredIcon(config.icons, button.icon),
      })),
    };
  }),
};

function configuredIcon(
  aliases: Record<string, Omit<ConfigIcon, 'alias'>> | undefined,
  button: ConfigIconReference | undefined,
): FaceUiIconAsset | undefined {
  if (button === false) return undefined;
  const buttonIcon = iconReference(button);
  const alias = buttonIcon.alias;
  const aliasedIcon = alias ? aliases?.[alias] : undefined;
  if (alias && !aliasedIcon) {
    throw new Error(`Unknown Face UI icon alias: ${alias}`);
  }
  const icon = { ...aliasedIcon, ...buttonIcon };
  if (!icon.src) return undefined;
  delete icon.alias;
  return icon as FaceUiIconAsset;
}

function iconReference(reference: ConfigIconReference | undefined): ConfigIcon {
  return typeof reference === 'string' ? { alias: reference } : reference || {};
}

export function transformActionForZone(
  buttonId: string,
  layoutId: string,
  axes: FaceUiAxes,
): { axis: BlockTransformAxis; mirror: boolean; direction: 1 | -1 } | null {
  const mirror = layoutId === 'mirror';
  const horizontal = mirror
    ? buttonId === 'horizontal' || buttonId === 'left' || buttonId === 'right'
    : buttonId === 'left' || buttonId === 'right';
  const vertical = mirror
    ? buttonId === 'vertical' || buttonId === 'up' || buttonId === 'down'
    : buttonId === 'up' || buttonId === 'down';
  if (!horizontal && !vertical) return null;

  // Rotation axes are perpendicular to the arrow but remain in the face plane.
  // Mirror axes follow the arrow direction in that same plane.
  const vector = mirror
    ? (horizontal ? axes.right : axes.up)
    : (horizontal ? axes.up : axes.right);
  const { axis, sign } = worldAxis(vector);
  const arrowDirection =
    buttonId === 'right' || buttonId === 'down' ? 1 : -1;
  return {
    axis,
    mirror,
    direction: (arrowDirection * sign) as 1 | -1,
  };
}

function worldAxis(vector: FaceUiAxisVector): {
  axis: BlockTransformAxis;
  sign: 1 | -1;
} {
  if (vector[0] !== 0) return { axis: 'x', sign: Math.sign(vector[0]) as 1 | -1 };
  if (vector[1] !== 0) return { axis: 'y', sign: Math.sign(vector[1]) as 1 | -1 };
  return { axis: 'z', sign: Math.sign(vector[2]) as 1 | -1 };
}
